
import { useState } from "react";
import { format } from "date-fns";
import { Trash2, ChevronDown, Star, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { type TranslationKey } from "@/utils/translations";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface ChatItemProps {
  chat: {
    id: string;
    title: string;
    content: string;
    created_at: string;
    is_favorite?: boolean;
  };
  t: Record<TranslationKey, string>;
  onToggleFavorite?: (chatId: string) => void;
}

export const ChatItem = ({ chat, t, onToggleFavorite }: ChatItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const chatContent = JSON.parse(chat.content);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get first few words of the question for preview
  const getPreview = (text: string, maxWords: number = 8) => {
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      const { error } = await supabase
        .from("saved_chats")
        .delete()
        .eq("id", chat.id);

      if (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No se pudo eliminar la conversación"
        });
        return;
      }

      toast({
        title: "Conversación eliminada",
        description: "La conversación se eliminó correctamente"
      });

      queryClient.invalidateQueries({ queryKey: ["saved-chats"] });
      setIsDeleteOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Ocurrió un error inesperado"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleFavorite = () => {
    if (onToggleFavorite) {
      onToggleFavorite(chat.id);
    }
  };

  const handleReopenConversation = () => {
    // TODO: Implement navigation to full conversation view
    toast({
      title: "Función próximamente",
      description: "La función de reabrir conversación estará disponible pronto"
    });
  };
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-100 transition-all duration-300 cursor-pointer group hover:shadow-xl hover:bg-slate-50 hover:-translate-y-1"
      style={{ 
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{ padding: '1.25rem' }}>
        {/* Header with preview and controls */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="flex items-start gap-2 mb-2">
              {chat.is_favorite && (
                <Star className="h-4 w-4 text-yellow-500 fill-current flex-shrink-0 mt-0.5" />
              )}
              <h3 className="font-semibold text-lg text-navy leading-tight group-hover:text-primary transition-colors">
                {getPreview(chatContent.question)}
              </h3>
            </div>
            <time className="text-sm text-coolGray">
              {format(new Date(chat.created_at), 'dd/MM/yyyy HH:mm')}
            </time>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Favorite Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFavorite();
              }}
              className={`rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-sm ${
                chat.is_favorite 
                  ? "text-yellow-500 hover:text-yellow-600 bg-yellow-50 hover:bg-yellow-100" 
                  : "text-coolGray hover:text-yellow-500 hover:bg-yellow-50"
              }`}
              style={{ minHeight: '44px', minWidth: '44px' }}
              aria-label={chat.is_favorite ? "Quitar de favoritos" : "Marcar como favorito"}
              title={chat.is_favorite ? "Quitar de favoritos" : "Marcar como favorito"}
            >
              <Star className={`h-4 w-4 ${chat.is_favorite ? "fill-current" : ""}`} />
            </Button>

            {/* Expand Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-coolGray hover:text-navy hover:bg-white/90 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-sm"
              style={{ minHeight: '44px', minWidth: '44px' }}
              aria-label={isExpanded ? "Contraer conversación" : "Expandir conversación"}
              title={isExpanded ? "Contraer conversación" : "Expandir conversación"}
            >
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`} 
              />
            </Button>
            
            {/* Delete Button */}
            <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-coolGray hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 border border-gray-200 hover:border-red-200 hover:shadow-sm"
                  style={{ minHeight: '44px', minWidth: '44px' }}
                  aria-label="Eliminar esta conversación"
                  title="Eliminar esta conversación"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Eliminar conversación</DialogTitle>
                  <DialogDescription>
                    ¿Estás seguro de que quieres eliminar esta conversación? Esta acción no se puede deshacer.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    onClick={() => setIsDeleteOpen(false)}
                    disabled={isDeleting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Eliminando..." : "Eliminar"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Expanded content with smooth animation */}
        {isExpanded && (
          <div className="space-y-4 pt-4 border-t border-gray-100 animate-fade-in">
            <div className="bg-primary/5 p-4 rounded-lg border-l-4 border-primary">
              <p className="font-medium text-sm text-navy mb-2">{t.question}</p>
              <p className="text-coolGray leading-relaxed text-base">{chatContent.question}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium text-sm text-navy mb-2">{t.answer}</p>
              <p className="text-coolGray leading-relaxed text-base">{chatContent.answer}</p>
            </div>
            
            {/* Action Button */}
            <div className="pt-2">
              <Button
                onClick={handleReopenConversation}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
              >
                <MessageSquare className="h-4 w-4" />
                Ver conversación completa
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
